import { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Plus, Minus, DollarSign, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt$ = (n, opts = {}) =>
  `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0, ...opts })}`;
const fmtShares = (n) =>
  Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

function formatSnapDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

/**
 * RebalancerSnapshotDiff
 * ----------------------
 * Renders the per-ticker + cash diff between a snapshot and the
 * user's current live portfolio. Two modes:
 *   • mode="compact"  — small inline summary line ("+12 EGGY,
 *                       -10 TRIN, cash -$182"). Used inside the
 *                       Restore confirm modal so the user sees
 *                       exactly what they're undoing.
 *   • mode="full"     — full grouped breakdown (changed / added /
 *                       removed + cash + total value). Used in the
 *                       standalone Compare panel.
 *
 * Self-fetches the diff via /api/portfolio/rebalancer/snapshots/
 * {snapshot_id}/diff so the parent only needs to pass the
 * snapshot_id.
 */
export default function RebalancerSnapshotDiff({ snapshotId, mode = 'full' }) {
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!snapshotId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setDiff(null);
      try {
        const { data } = await axios.get(
          `${API}/portfolio/rebalancer/snapshots/${snapshotId}/diff`,
          { withCredentials: true },
        );
        if (!cancelled) setDiff(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Could not load diff');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [snapshotId]);

  if (!snapshotId) return null;
  if (loading) {
    return <div className="h-12 rounded-md shimmer" data-testid="rebalancer-diff-loading" />;
  }
  if (error) {
    return (
      <div className="text-xs text-rose-500" data-testid="rebalancer-diff-error">
        {error}
      </div>
    );
  }
  if (!diff) return null;

  if (!diff.has_changes) {
    return (
      <div
        className="rounded-md border border-emerald-400/50 bg-emerald-50/70 dark:bg-emerald-950/20 px-3 py-2 text-xs flex items-center gap-2"
        data-testid="rebalancer-diff-no-changes"
      >
        <Sparkles className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
        <span className="text-emerald-700 dark:text-emerald-300">
          Nothing changed since this snapshot — your portfolio is identical to the
          {' '}{formatSnapDate(diff.created_at)} save.
        </span>
      </div>
    );
  }

  // Compact mode — one-line summary, used inside the Restore modal.
  if (mode === 'compact') {
    const parts = [];
    diff.changed_existing.slice(0, 3).forEach((r) => {
      const sign = r.delta_shares > 0 ? '+' : '';
      parts.push(`${sign}${fmtShares(r.delta_shares)} ${r.ticker}`);
    });
    diff.added.slice(0, 2).forEach((r) => {
      parts.push(`+${fmtShares(r.current_shares)} ${r.ticker} (new)`);
    });
    diff.removed.slice(0, 2).forEach((r) => {
      parts.push(`sold ${r.ticker}`);
    });
    const totalMoves = diff.changed_existing.length + diff.added.length + diff.removed.length;
    const shown = Math.min(parts.length, totalMoves);
    if (totalMoves > shown) parts.push(`+${totalMoves - shown} more`);

    return (
      <div
        className="rounded-md border border-amber-400/60 bg-amber-50/70 dark:bg-amber-950/20 px-3 py-2 text-xs"
        data-testid="rebalancer-diff-compact"
      >
        <div className="font-semibold text-amber-800 dark:text-amber-200 mb-1">
          Since you started testing:
        </div>
        <div className="text-amber-800/90 dark:text-amber-200/80 font-mono">
          {parts.join(', ')}
          {Math.abs(diff.cash_delta) > 0.005 && (
            <>
              , cash{' '}
              <span className={diff.cash_delta < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-300'}>
                {diff.cash_delta > 0 ? '+' : '−'}{fmt$(Math.abs(diff.cash_delta), { minimumFractionDigits: 0 })}
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  // Full mode — grouped breakdown.
  return (
    <div className="space-y-3" data-testid="rebalancer-diff-full">
      <div className="text-xs text-muted-foreground">
        Snapshot saved {formatSnapDate(diff.created_at)}
      </div>

      {/* Headline summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
        <DiffStatCard
          label="Position moves"
          value={diff.changed_existing.length + diff.added.length + diff.removed.length}
          neutral
          testid="diff-stat-moves"
        />
        <DiffStatCard
          label="Cash change"
          value={`${diff.cash_delta > 0 ? '+' : diff.cash_delta < 0 ? '−' : ''}${fmt$(Math.abs(diff.cash_delta), { minimumFractionDigits: 0 })}`}
          positive={diff.cash_delta > 0.005}
          negative={diff.cash_delta < -0.005}
          testid="diff-stat-cash"
        />
        <DiffStatCard
          label="Total value change"
          value={`${diff.total_value_delta > 0 ? '+' : diff.total_value_delta < 0 ? '−' : ''}${fmt$(Math.abs(diff.total_value_delta), { minimumFractionDigits: 0 })}`}
          positive={diff.total_value_delta > 0.005}
          negative={diff.total_value_delta < -0.005}
          subtitle="at current prices"
          testid="diff-stat-total"
        />
      </div>

      {diff.changed_existing.length > 0 && (
        <DiffSection
          title="Changed positions"
          icon={<TrendingUp className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />}
          rows={diff.changed_existing}
          renderRow={(r) => (
            <DiffRow
              key={r.ticker}
              ticker={r.ticker}
              left={`${fmtShares(r.snapshot_shares)} → ${fmtShares(r.current_shares)}`}
              delta={r.delta_shares}
              deltaSuffix="shares"
              deltaValue={r.delta_value}
            />
          )}
        />
      )}

      {diff.added.length > 0 && (
        <DiffSection
          title="Added (new positions)"
          icon={<Plus className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />}
          rows={diff.added}
          renderRow={(r) => (
            <DiffRow
              key={r.ticker}
              ticker={r.ticker}
              left={`bought ${fmtShares(r.current_shares)} sh @ ${fmt$(r.current_price, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              delta={r.delta_shares}
              deltaSuffix="shares"
              deltaValue={r.delta_value}
            />
          )}
        />
      )}

      {diff.removed.length > 0 && (
        <DiffSection
          title="Removed (fully sold)"
          icon={<Minus className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />}
          rows={diff.removed}
          renderRow={(r) => (
            <DiffRow
              key={r.ticker}
              ticker={r.ticker}
              left={`was ${fmtShares(r.snapshot_shares)} sh`}
              delta={r.delta_shares}
              deltaSuffix="shares"
              deltaValue={r.delta_value}
            />
          )}
        />
      )}
    </div>
  );
}

function DiffStatCard({ label, value, positive, negative, neutral, subtitle, testid }) {
  let tone = 'text-foreground';
  if (positive) tone = 'text-emerald-700 dark:text-emerald-300';
  else if (negative) tone = 'text-rose-600 dark:text-rose-400';
  else if (neutral) tone = 'text-amber-700 dark:text-amber-300';
  return (
    <div className="rounded-md border bg-background/60 px-2 py-1.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-mono font-bold text-sm ${tone}`}>{value}</div>
      {subtitle && (
        <div className="text-[10px] text-muted-foreground/80">{subtitle}</div>
      )}
    </div>
  );
}

function DiffSection({ title, icon, rows, renderRow }) {
  return (
    <div className="rounded-md border bg-background/60 px-3 py-2">
      <div className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <h4 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          {title} <span className="text-muted-foreground/70">· {rows.length}</span>
        </h4>
      </div>
      <div className="space-y-1">
        {rows.map(renderRow)}
      </div>
    </div>
  );
}

function DiffRow({ ticker, left, delta, deltaSuffix, deltaValue }) {
  const positive = delta > 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <div
      className="flex items-center justify-between gap-2 text-[11px] font-mono py-0.5"
      data-testid={`diff-row-${ticker}`}
    >
      <span className="flex items-center gap-2 min-w-0">
        <span className="font-bold w-12 shrink-0">{ticker}</span>
        <span className="text-muted-foreground truncate">{left}</span>
      </span>
      <span className={`flex items-center gap-1.5 shrink-0 ${positive ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-400'}`}>
        <Icon className="w-3 h-3" />
        <span className="font-bold">{positive ? '+' : ''}{fmtShares(delta)} {deltaSuffix}</span>
        {Math.abs(deltaValue) > 0.005 && (
          <span className="text-muted-foreground/80 inline-flex items-center">
            <DollarSign className="w-2.5 h-2.5" />
            {Number(Math.abs(deltaValue)).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        )}
      </span>
    </div>
  );
}
